import axios from 'axios';
import { toast } from 'react-toastify';
import secureStorage from '@/utils/secureStorage';

// Use environment variable for API URL, fallback to proxy path for development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
// const API_BASE_URL = 'http://89.116.20.149:8003/api/v1';
// const API_BASE_URL = 'https://api.addrcafe.nexgensis.com/api/v1';
// const API_BASE_URL = 'http://192.168.2.26:8001/api/v1'


// Export for use in other services that need direct URL access
export { API_BASE_URL };

// Configuration
const REQUEST_TIMEOUT = 30000; // 30 seconds
const MAX_RETRY_ATTEMPTS = 1;

// Token refresh state management with mutex lock
let isRefreshing = false;
let refreshPromise = null;
let failedQueue = [];

/**
 * Process queued requests after token refresh
 * Uses copy of queue to prevent race condition
 */
const processQueue = (error, token = null) => {
  const queue = [...failedQueue];
  failedQueue = []; // Clear immediately to prevent race condition

  queue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
};

/**
 * Refresh the access token using refresh token
 * Returns a singleton promise to prevent multiple refresh requests
 */
const refreshAccessToken = async () => {
  const refreshToken = secureStorage.getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Use a fresh axios instance to avoid interceptor loops
  const response = await axios.post(
    `${API_BASE_URL}/auth/token/refresh/`,
    { refresh: refreshToken },
    { timeout: REQUEST_TIMEOUT }
  );

  const { access, refresh } = response.data;
  if (!access) {
    throw new Error('No access token in response');
  }

  secureStorage.setAccessToken(access);
  // Save the rotated refresh token (backend blacklists the old one)
  if (refresh) {
    secureStorage.setRefreshToken(refresh);
  }
  return access;
};

// Create axios instance with timeout
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = secureStorage.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If sending FormData, let axios handle the Content-Type with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip interceptor logic for logout requests
    if (originalRequest.skipAuthInterceptor) {
      return Promise.reject(error);
    }

    // Handle network errors
    if (!error.response) {
      const errorMessage =
        error.code === 'ECONNABORTED'
          ? 'Request timed out. Please try again later.'
          : 'Something went wrong. Please check your connection and try again.';
      toast.error(errorMessage);
      error.userMessage = errorMessage;
      return Promise.reject(error);
    }

    // Skip 401 handling for login requests (failed login credentials)
    const isLoginRequest = originalRequest.url?.includes('/auth/login/');

    // Handle 401 Unauthorized (but not for login failures)
    if (error.response.status === 401 && !originalRequest._retry && !isLoginRequest) {
      const refreshToken = secureStorage.getRefreshToken();

      if (!refreshToken) {
        // No refresh token, show toast and redirect to login
        toast.error('Session expired. Please login again.');
        secureStorage.clearAuthData();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Create singleton refresh promise
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken();
        }

        const newToken = await refreshPromise;
        refreshPromise = null;

        // Process queued requests
        processQueue(null, newToken);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        refreshPromise = null;
        // Refresh token failed or expired, show toast and logout user
        toast.error('Session expired. Please login again.');
        processQueue(refreshError, null);
        isRefreshing = false;
        secureStorage.clearAuthData();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle 401 that already retried (shouldn't happen often)
    // But skip for login requests - those are just invalid credentials
    if (error.response.status === 401 && originalRequest._retry && !isLoginRequest) {
      toast.error('Session expired. Please login again.');
      secureStorage.clearAuthData();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle other errors - show user-friendly messages
    // Include 401 from login requests (invalid credentials)
    if (error.response.status !== 401 || isLoginRequest) {
      const status = error.response.status;
      let errorMessage;

      switch (status) {
        case 400:
          errorMessage =
            error.response.data?.message ||
            error.response.data?.detail ||
            error.response.data?.error ||
            'Invalid request. Please check your input.';
          break;
        case 401:
          // For login requests, provide specific message
          errorMessage =
            error.response.data?.message ||
            error.response.data?.detail ||
            error.response.data?.error ||
            'Invalid email or password.';
          break;
        case 403:
          errorMessage = 'You do not have permission to perform this action.';
          break;
        case 404:
          errorMessage = 'The requested resource was not found.';
          break;
        case 500:
        case 502:
        case 503:
          errorMessage = 'Something went wrong. Please try again later.';
          break;
        default:
          errorMessage =
            error.response.data?.message ||
            error.response.data?.detail ||
            error.response.data?.error ||
            'Something went wrong. Please try again later.';
      }

      // Don't show toast for login errors - let the login component handle it
      if (!isLoginRequest) {
        toast.error(errorMessage);
      }

      // Attach the user-friendly message to the error for components to use
      error.userMessage = errorMessage;
    }

    return Promise.reject(error);
  }
);

/**
 * Initialize API - restore session from refresh token if needed
 * Call this on app startup
 */
export const initializeApi = async () => {
  // Reset refresh state on initialization (e.g. after page reload)
  isRefreshing = false;
  refreshPromise = null;
  failedQueue = [];

  const refreshToken = secureStorage.getRefreshToken();
  const accessToken = secureStorage.getAccessToken();

  // If we have a refresh token but no access token, try to restore session
  if (refreshToken && !accessToken) {
    try {
      await refreshAccessToken();
      return true;
    } catch {
      secureStorage.clearAuthData();
      return false;
    }
  }

  return !!accessToken;
};

export default api;
